import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Container,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { getLocalStorageItem, setLocalStorageItem } from "../../_helpers";
import { useTranslation } from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import axios from "axios";
import { apps, LOCAL_STORAGE_GRID_KEYS } from "../../_constants";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import DataGridV2 from "../../components/DataGridV2";
import { INITIAL_ROW_SELECTION_MODEL } from "../../components/DataGridV2/constants";
import { getSubContractorOverviewColumns } from "./utils";

const { webApiUrl } = window.appConfig;

export default function SubContractorOverview(props) {
  const { history } = props;
  const apiRef = useGridApiRef();
  const { t , i18n} = useTranslation();
  const [overviewData, setOverviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { selected } = useSelector((state) => state.buildings);
  const app = useSelector((state) => state.app);
  const classes = useStyles();
  const keyPrefix = app === apps.aftercare ? "AFTERCARE" :  app === apps.constructionQuality ? "CONSTRUCTION_QUALITY" : "GENERAL";
  const [rowSelectionModel, setRowSelectionModel] = useState(INITIAL_ROW_SELECTION_MODEL);
  const [paginationModel, setPaginationModel] = useState(() => getLocalStorageItem(
      LOCAL_STORAGE_GRID_KEYS.SUB_CONTRACTOR_OVERVIEW_PAGINATION[keyPrefix], undefined
  ))
  useEffect(() => {
    if (selected)
      (async () => {
        setLoading(true);
        const { data: dataFromServer } = await axios.get(`${webApiUrl}api/RepairRequest/GetWorkOrdersOverview/?app=${app}`);
        const data = dataFromServer.map((ws) => {
          return {
            id: ws.organisationId,
            organisatonId: ws.organisationId,
            subContractorName: ws.name,
            newWorkOrder: ws.newCount,
            inProgressWorkOrders: ws.inProgressCount,
            overdueWorkOrders : ws.overdueCount,
          };
        });

        setOverviewData(data);
        setLoading(false);
      })();
  }, [selected]);

  const redirectToWorkOrderPage = (
    cellValue,
    columnName,
    organisationId
  ) => {
    let url = "/werk/" + selected.projectNo + "/werkbonnen";
    let contractor = cellValue;
    if (app === apps.aftercare) {
      url = "/nazorg/werkbonnen";
    }
    const overview = overviewData.find((a) => a.organisatonId === organisationId);
    
    contractor = app !== apps.resolverModule && Object.assign(overview, { id: overview.organisatonId });
    history.push({
      pathname: url,
      state: columnName === "subContractorName" && app === apps.resolverModule ? {} : { filter: columnName, contractor },
    });
  };

 const columns = getSubContractorOverviewColumns({t,keyPrefix,i18n,apiRef,redirectToWorkOrderPage})

      const onPaginationModelChange = useCallback((newPaginationModel) => {
        setPaginationModel(newPaginationModel)
        setLocalStorageItem(
          LOCAL_STORAGE_GRID_KEYS.SUB_CONTRACTOR_OVERVIEW_PAGINATION[keyPrefix],
          newPaginationModel
        );
      }, [keyPrefix])

        const getSubContractorOverviewForPrintAndDownload = ({ isAllSelected = true, idsToExport = [] }) => {
          const userSelectedRows = isAllSelected ? overviewData : idsToExport.map(id => overviewData?.find(item => item?.organisatonId === id));
          return userSelectedRows;
        };

  return (
      <Container maxWidth={false} className={classes.mainContainer}>
        <Box display="flex" flexDirection="column" sx={{ height: "100%" }}>
        <Box bgcolor={'white'} py={1} px={2} display={'flex'} alignItems={'center'} >
          <IconButton  edge='start' aria-label="GoBack" color="inherit" size="large" onClick={() => history.goBack()}>
            <ArrowBack />
          </IconButton>
          <Typography className={classes.headerTitle} variant='h5'>{t("subcontractor.overview.page.title")}</Typography>
        </Box>
        <Box  sx={{ flex: 1, minHeight: 0 }}>
          <DataGridV2
            localSearchIdentifier={LOCAL_STORAGE_GRID_KEYS.SUB_CONTRACTOR_OVERVIEW_SEARCH_TEXT[keyPrefix]}
            localColumnFilterIdentifier={LOCAL_STORAGE_GRID_KEYS.SUB_CONTRACTOR_OVERVIEW_COLUMN_FILTER[keyPrefix]}
            localColumnSortIdentifier={LOCAL_STORAGE_GRID_KEYS.SUB_CONTRACTOR_OVERVIEW_COLUMN_SORT[keyPrefix]}
            localColumnOrderIdentifier={LOCAL_STORAGE_GRID_KEYS.SUB_CONTRACTOR_OVERVIEW_COLUMN_ORDER[keyPrefix]}
            localColumnWidthIdentifier={LOCAL_STORAGE_GRID_KEYS.SUB_CONTRACTOR_OVERVIEW_COLUMN_WIDTH[keyPrefix]}
            localColumnVisibilityIdentifier={LOCAL_STORAGE_GRID_KEYS.SUB_CONTRACTOR_OVERVIEW_COLUMN_VISIBILITY[keyPrefix]}
            apiRef={apiRef}
            checkboxSelection
            rows={overviewData}
            loading={loading}
            columns={columns}
            pagination
            filterMode="client"
            {...(!!paginationModel && { paginationModel })}
            onPaginationModelChange={onPaginationModelChange}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newRowSelectionModel) => {
              setRowSelectionModel(newRowSelectionModel);
            }}
            toolbarProps={{
              selectedRows: rowSelectionModel,
              getData:getSubContractorOverviewForPrintAndDownload
            }}
          />
          </Box>
          </Box>
      </Container>
  );
}

const useStyles = makeStyles((theme) => ({
  "@global": {
    ".MuiButtonBase-root:focus": {
      outline: 0,
    },
  },
  momentLabelStyle: {
    [theme.breakpoints.up("sm")]: {
      wordBreak: "break-all",
    },
  },
  customTableCell: {
    cursor: "pointer",
    [theme.breakpoints.down("md")]: {
      fontSize: "12px !important",
    },
    [theme.breakpoints.up("sm")]: {
      whiteSpace: "nowrap",
    },
    fontSize: "14px !important",
  },
  tableText: {
    cursor: "pointer",
  },
  headerTitle: {
    color: theme.palette.grey[900],
    fontWeight: "bold",
    fontSize: 16,
    [theme.breakpoints.down("md")]: {
      fontSize: 10,
    },
  },
  mainContainer: {
    height: "100%",
    width: "100%",
    overflow: "auto",
    padding: 0,
  },
  dataTable: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    "& > .MuiPaper-root": {
      flex: "0 1 auto",
    },
    "& > .MuiToolbar-root.MuiToolbar-regular": {
      display: "flex",
      "& > div": {
        padding: 0,
        textAlign: "right",
        flex: "1 0 auto",
        "& .MuiTypography-root": {
          textAlign: "left",
        },
      },
    },
    "& .MuiTableCell-root": {
      padding: theme.spacing(0, 0.5, 0, 0),
      "&.MuiTableCell-paddingCheckbox": {
        paddingLeft: theme.spacing(0.5),
        "& > div": {
          justifyContent: "center",
        },
        "& .MuiCheckbox-root": {
          padding: theme.spacing(0.25),
        },
      },
      "&.MuiTableCell-head": {
        fontWeight: "bold",
      },
    },
  },
}));
